<template>

  <b-row>
    <b-col>
      <b-card title="Filters">
        <b-form @keyup.enter="filterData">
          <b-row>
            <b-col>
              <b-form-group
                label="Type"
                label-for="type"
              >
                <v-select
                  v-model="selectedFilterType"
                  :options="typeOptions"
                  :multiple="true"
                  :clearable="false"
                  input-id="type"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Date range"
                label-for="date-range"
              >
                <flat-pickr
                  id="date-range"
                  v-model="filters.dateRange"
                  class="form-control"
                  :class="{ 'is-invalid': dateRangeError }"
                  :config="datePickerConfig"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                label="Callback status"
                label-for="callback-status"
              >
                <v-select
                  v-model="selectedFilterCallbackStatus"
                  :options="callbackStatusOptions"
                  :multiple="true"
                  :clearable="false"
                  input-id="callback-status"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Reference type"
                label-for="reference-type"
              >
                <v-select
                  v-model="selectedFilterReferenceType"
                  :options="referenceTypeOptions"
                  :multiple="true"
                  :clearable="false"
                  input-id="reference-type"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                label="Document number"
                label-for="document-number"
                class="full-width"
              >
                <b-input-group>
                  <b-form-tags
                    id="document-number"
                    v-model="filters.document_number"
                    label="Document number"
                    :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
                    separator=" ,;"
                    placeholder="Enter document numbers separated by space, comma or semicolon"
                    no-add-on-enter
                    add-on-change
                    clearable
                  />
                  <b-input-group-append v-if="filters.document_number.length">
                    <b-button
                      id="filter-reset"
                      variant="secondary"
                      size="sm"
                      @click="filters.document_number = []"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                label="Container code"
                label-for="container-code"
                class="full-width"
              >
                <b-input-group>
                  <b-form-tags
                    id="container-code"
                    v-model="filters.container_code"
                    label="Container code"
                    :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
                    separator=" ,;"
                    placeholder="Enter container codes separated by space, comma or semicolon"
                    no-add-on-enter
                    add-on-change
                    clearable
                  />
                  <b-input-group-append v-if="filters.container_code.length">
                    <b-button
                      id="filter-reset"
                      variant="secondary"
                      size="sm"
                      @click="filters.container_code = []"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <div class="d-flex justify-content-between">
                <b-form-group
                  label="Parcel tracking number"
                  label-for="parcel-tracking-number"
                  class="full-width"
                >
                  <b-input-group>
                    <b-form-tags
                      id="parcel-tracking-number"
                      v-model="filters.parcel_tracking_number"
                      label="Parcel tracking number"
                      :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
                      separator=" ,;"
                      placeholder="Enter tracking codes separated by space, comma or semicolon"
                      no-add-on-enter
                      add-on-change
                      clearable
                    />
                    <b-input-group-append v-if="filters.parcel_tracking_number.length">
                      <b-button
                        id="filter-reset"
                        variant="secondary"
                        size="sm"
                        @click="filters.parcel_tracking_number = []"
                      >
                        <feather-icon icon="XIcon" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
                <div class="ml-2">
                  <b-button
                    id="apply-filters-btn"
                    class="mt-2"
                    type="button"
                    variant="primary"
                    @click="filterData()"
                  >
                    Filter
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BButton,
  BFormTags,
  BInputGroupAppend,
  BInputGroup,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import vSelect from 'vue-select'
import { parseFilters } from '@core/utils/filter'
import { EventBus } from '@core/utils/utils'

export default {
  components: {
    BInputGroup,
    BInputGroupAppend,
    BFormTags,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    flatPickr,
    BCard,
    vSelect,
  },
  props: ['types'],
  data() {
    return {
      filters: {
        document_number: [],
        container_code: [],
        parcel_tracking_number: [],
        type: '',
        callback_status: '',
        reference_type: '',
        dateRange: this.getLastThreeDays(),
      },
      selectedFilterType: null,
      selectedFilterCallbackStatus: null,
      selectedFilterReferenceType: null,
      dateRangeError: false,
    }
  },
  computed: {
    typeOptions() {
      const seen = new Set()
      const types = [{
        label: 'N/A',
        value: '',
      }]
      this.types.forEach(item => {
        if (!seen.has(item.value)) {
          seen.add(item.value)
          types.push(item)
        }
      })
      return types
    },
    callbackStatusOptions() {
      return [
        { label: 'Succeeded', value: 'succeeded' },
        { label: 'Failed', value: 'failed' },
        { label: 'Not sent', value: 'not_sent' },
      ]
    },
    referenceTypeOptions() {
      return [
        { label: 'Shipment', value: 'shipment' },
        { label: 'Container', value: 'container' },
        { label: 'Parcel', value: 'parcel' },
      ]
    },
    formattedValues() {
      const result = []
      this.selectedFilterType.forEach(item => {
        result.push(item.value)
      })
      return result
    },
    callbackStatusValues() {
      const result = []
      this.selectedFilterCallbackStatus.forEach(item => {
        result.push(item.value)
      })
      return result
    },
    referenceTypeValues() {
      const result = []
      this.selectedFilterReferenceType.forEach(item => {
        result.push(item.value)
      })
      return result
    },
    datePickerConfig() {
      return {
        mode: 'range',
        dateFormat: 'Y-m-d',
        onChange: this.handleDateRangeChange,
      }
    },
  },
  watch: {
    'filters.dateRange'(val) {
      if (typeof val === 'string' && val.includes(' to ')) {
        this.dateRangeError = false
      } else this.dateRangeError = !(Array.isArray(val) && val.length === 2 && val[0] && val[1])
    },
  },
  mounted() {
    this.filterData()
  },
  methods: {
    filterData() {
      if (this.selectedFilterType) {
        this.filters.type = this.formattedValues
      }
      if (this.selectedFilterCallbackStatus) {
        this.filters.callback_status = this.callbackStatusValues
      }
      if (this.selectedFilterReferenceType) {
        this.filters.reference_type = this.referenceTypeValues
      }

      const filters = { ...this.filters }

      filters.document_number = filters.document_number.join(',')
      filters.container_code = filters.container_code.join(',')
      filters.parcel_tracking_number = filters.parcel_tracking_number.join(',')

      if (Array.isArray(filters.dateRange)) {
        const [start, end] = filters.dateRange

        if (start && end) {
          filters.dateRange = `${start} to ${end}`
          this.dateRangeError = false
        } else if (start && !end) {
          filters.dateRange = start
          this.dateRangeError = false
        } else {
          this.dateRangeError = true
          return
        }
      } else if (typeof filters.dateRange === 'string') {
        if (filters.dateRange.includes(' to ')) {
          this.dateRangeError = false
        } else if (filters.dateRange.length) {
          this.dateRangeError = false
        } else {
          this.dateRangeError = true
          return
        }
      }

      filters.page = 1
      EventBus.$emit('updateFilters', parseFilters(filters))
    },
    getLastThreeDays() {
      const today = new Date()
      const threeDaysAgo = new Date()
      threeDaysAgo.setDate(today.getDate() - 3)
      const formatDate = date => date.toISOString().split('T')[0]
      return `${formatDate(threeDaysAgo)} to ${formatDate(today)}`
    },
    handleDateRangeChange(selectedDates, dateStr, instance) {
      if (selectedDates.length === 2) {
        const [start, end] = selectedDates
        const maxDays = 365
        const diffInDays = (end - start) / (1000 * 60 * 60 * 24)

        if (diffInDays > maxDays) {
          instance.clear()
          this.filters.dateRange = []
          this.dateRangeError = true

          if (this.$bvToast) {
            this.$bvToast.toast(`Please select a date range of ${maxDays} days or less.`, {
              title: 'Invalid Date Range',
              variant: 'danger',
              solid: true,
            })
          }
        } else {
          this.dateRangeError = false
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select';

#apply-filters-btn {
  padding-top: .63rem;
  padding-bottom: .63rem;
}
</style>
